import { useQuery } from '@tanstack/react-query';
import { supabase, GalleryAllImage } from '@/lib/supabase';
import { GalleryDataAccess, UnifiedGalleryImage } from '@/lib/galleryDataAccess';
import { BGVGallery, GalleryAllImages, ReviewStatus, FranchiseRegion } from '@/types/gallery';

// Unified function to fetch all images - now using GalleryDataAccess as primary method
const fetchGalleryAllImages = async (galleryId: number): Promise<GalleryAllImage[]> => {
  try {
    console.log(`🔄 Fetching images for gallery ${galleryId} using unified data access...`);
    
    // PRIMARY: Use new unified data access layer
    const unifiedImages = await GalleryDataAccess.getGalleryImages(galleryId);
    
    if (unifiedImages && unifiedImages.length > 0) {
      console.log(`✅ Retrieved ${unifiedImages.length} images via unified system`);
      
      // Convert UnifiedGalleryImage to GalleryAllImage for compatibility
      const compatibleImages: GalleryAllImage[] = unifiedImages.map(img => ({
        image_type: img.image_type,
        original_id: img.original_id,
        gallery_id: img.gallery_id,
        image_url: img.image_url,
        caption: img.caption,
        review_status: img.review_status,
        created_at: img.created_at,
        job_id: img.job_id,
        job_number: img.job_number,
        franchise_region: img.franchise_region,
        is_cover: img.is_cover,
        sort_order: img.sort_order,
        file_name: img.file_name,
        jobber_web_uri: img.jobber_web_uri
      }));
      
      return compatibleImages;
    }
    
    // FALLBACK 1: Try database view (if it exists)
    console.log('⚠️ No unified images found, trying gallery_all_images view...');
    const { data: viewData, error: viewError } = await supabase
      .from('gallery_all_images')
      .select('*')
      .eq('gallery_id', galleryId)
      .order('created_at', { ascending: false });
    
    if (!viewError && viewData && viewData.length > 0) {
      console.log(`✅ Retrieved ${viewData.length} images via database view`);
      return viewData;
    }
    
    // FALLBACK 2: Use legacy gallery_photos (for backward compatibility)
    console.log('⚠️ Database view unavailable, falling back to gallery_photos...');
    const { data: photosData, error: photosError } = await supabase
      .from('gallery_photos')
      .select('*')
      .eq('gallery_id', galleryId)
      .order('sort_order', { ascending: true });
    
    if (photosError) {
      console.warn('❌ gallery_photos table also unavailable:', photosError);
      return [];
    }
    
    if (photosData && photosData.length > 0) {
      console.log(`✅ Retrieved ${photosData.length} legacy photos via gallery_photos`);
      
      // Transform gallery_photos to match GalleryAllImage interface
      const transformedPhotos: GalleryAllImage[] = photosData.map(photo => ({
        image_type: 'gallery_photo' as const,
        original_id: photo.id,
        gallery_id: photo.gallery_id,
        image_url: photo.image_url,
        caption: photo.caption,
        review_status: 'approved' as const, // Direct uploads are always approved
        created_at: photo.created_at,
        sort_order: photo.sort_order
      }));
      
      return transformedPhotos;
    }
    
    console.log('ℹ️ No images found in any source for gallery', galleryId);
    return [];
    
  } catch (error) {
    console.error('❌ Error fetching gallery images:', error);
    return [];
  }
};

// BGV API functions
const fetchBGVGalleries = async (options: {
  searchTerm?: string;
  reviewStatus?: ReviewStatus;
  franchiseRegion?: FranchiseRegion;
}): Promise<BGVGallery[]> => {
  try {
    let query = supabase
      .from('galleries')
      .select('*')
      .order('created_on', { ascending: false });

    // Apply filters with enhanced search including job numbers
    if (options.searchTerm) {
      const cleanSearchTerm = options.searchTerm.replace(/[#]/g, ''); // Remove # symbol for job number search
      const isNumericSearch = /^\d+$/.test(cleanSearchTerm);
      
      if (isNumericSearch) {
        // If search term is numeric, prioritize job number search but also include text search
        query = query.or(`job_number.eq.${cleanSearchTerm},title.ilike.%${options.searchTerm}%,description.ilike.%${options.searchTerm}%`);
      } else {
        // Standard text search across title, description, and job number (as text)
        query = query.or(`title.ilike.%${options.searchTerm}%,description.ilike.%${options.searchTerm}%,job_number::text.ilike.%${cleanSearchTerm}%`);
      }
    }

    if (options.reviewStatus) {
      query = query.eq('review_status', options.reviewStatus);
    }

    if (options.franchiseRegion) {
      query = query.eq('franchise_region', options.franchiseRegion);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching BGV galleries:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Error in fetchBGVGalleries:', error);
    throw error;
  }
};

const fetchBGVGalleryImages = async (galleryId: number): Promise<GalleryAllImages[]> => {
  try {
    const { data, error } = await supabase
      .from('gallery_all_images')
      .select('*')
      .eq('gallery_id', galleryId)
      .order('sort_order', { ascending: true });

    if (error) {
      console.error('Error fetching BGV gallery images:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Error in fetchBGVGalleryImages:', error);
    throw error;
  }
};

// Track image usage for social media
const trackImageUsage = async (imageIds: number[], galleryId: number) => {
  try {
    // Update the used_for_social_media flag for the gallery
    const { error: galleryError } = await supabase
      .from('galleries')
      .update({ used_for_social_media: true })
      .eq('id', galleryId);

    if (galleryError) {
      console.error('Error updating gallery social media usage:', galleryError);
    }

    // Log the usage for audit purposes
    console.log(`Tracked social media usage for gallery ${galleryId}, images:`, imageIds);
  } catch (error) {
    console.error('Error tracking image usage:', error);
  }
};

interface UseGalleryImagesOptions {
  enabled?: boolean;
}

interface UseBGVGalleriesOptions {
  searchTerm?: string;
  reviewStatus?: ReviewStatus;
  franchiseRegion?: FranchiseRegion;
}

interface UseBGVGalleryImagesOptions {
  galleryId?: number;
  enabled?: boolean;
}

export const useGalleryImages = (galleryId: number | null, options: UseGalleryImagesOptions = {}) => {
  return useQuery({
    queryKey: ['gallery-images', galleryId],
    queryFn: () => galleryId ? fetchGalleryAllImages(galleryId) : Promise.resolve([]),
    enabled: !!galleryId && (options.enabled !== false),
    staleTime: 30000, // Consider data stale after 30 seconds
    refetchOnWindowFocus: false
  });
};

// BGV-specific hooks
export const useBGVGalleries = (options: UseBGVGalleriesOptions = {}) => {
  return useQuery({
    queryKey: ['bgv-galleries', options],
    queryFn: () => fetchBGVGalleries(options),
    staleTime: 60000, // Consider data stale after 1 minute
    refetchOnWindowFocus: false
  });
};

export const useBGVGalleryImages = (options: UseBGVGalleryImagesOptions = {}) => {
  return useQuery({
    queryKey: ['bgv-gallery-images', options.galleryId],
    queryFn: () => options.galleryId ? fetchBGVGalleryImages(options.galleryId) : Promise.resolve([]),
    enabled: !!options.galleryId && (options.enabled !== false),
    staleTime: 30000, // Consider data stale after 30 seconds
    refetchOnWindowFocus: false
  });
};

export { fetchGalleryAllImages, fetchBGVGalleries, fetchBGVGalleryImages, trackImageUsage };